import { injectAesthetic } from '../shared-styles.ts';

export class AppLineChart extends HTMLElement {
    private _data: number[] = [];
    private _labels: string[] = []; // Optional labels for tooltips
    private _title: string = '';
    
    private svgElement: SVGSVGElement | null = null;
    private pathElement: SVGPathElement | null = null;
    private tooltip: HTMLDivElement | null = null;
    private hoverDot: HTMLDivElement | null = null;
    private interactiveArea: SVGRectElement | null = null;

    static get observedAttributes() {
        return ['chart-title', 'hide-axis'];
    }

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    attributeChangedCallback(name: string, oldVal: string, newVal: string) {
        if (name === 'chart-title') {
            this._title = newVal;
        } else if (name === 'hide-axis') {
            const axisEl = this.shadowRoot?.querySelector('#x-axis-labels') as HTMLElement;
            if (axisEl) {
                axisEl.style.display = newVal === 'true' || newVal === '' ? 'none' : 'block';
            }
        }
    }

    connectedCallback() {
        this.render();
        this.setupInteractions();
    }

    public set chartData(data: { values: number[], labels?: string[] }) {
        this._data = data.values || [];
        this._labels = data.labels || [];
        if (this.isConnected) {
            this.drawChart();
        }
    }

    private drawChart() {
        if (!this.svgElement || !this.pathElement || this._data.length === 0) return;

        const width = 96;
        const height = 30; // Pad top and bottom
        
        const max = Math.max(...this._data, 0.1);
        const min = 0; // Or Math.min(...this._data) if we want relative baseline
        
        const points = this._data.map((val, i) => {
            const x = 2 + (i / (Math.max(this._data.length - 1, 1))) * width;
            const y = 35 - ((val - min) / (max - min)) * height;
            return { x, y, val, label: this._labels[i] || `Point ${i}` };
        });

        // Store points for hover interaction
        (this as any)._points = points;

        const pathStr = `M${points.map(p => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' L')}`;
        this.pathElement.setAttribute('d', pathStr);

        const axisEl = this.shadowRoot?.querySelector('#x-axis-labels') as HTMLElement;
        if (axisEl && this._labels.length > 0) {
            axisEl.innerHTML = `
                <div style="display: flex; justify-content: space-between; font-size: 0.65rem; color: var(--text-dim); margin-top: 8px;">
                    <span>${this._labels[0]}</span>
                    <span>${this._labels[Math.floor(this._labels.length/2)]}</span>
                    <span>${this._labels[this._labels.length-1]}</span>
                </div>
            `;
            if (this.hasAttribute('hide-axis') && this.getAttribute('hide-axis') !== 'false') {
                axisEl.style.display = 'none';
            } else {
                axisEl.style.display = 'block';
            }
        }
    }

    private setupInteractions() {
        if (!this.interactiveArea || !this.svgElement || !this.hoverDot || !this.tooltip) return;

        let isBrushing = false;
        let startX = 0;
        let brushStartSvgX = 0;
        const brushRect = this.shadowRoot?.querySelector('#brush-selection') as SVGRectElement;

        this.interactiveArea.addEventListener('mousedown', (e) => {
            isBrushing = true;
            const rect = this.svgElement!.getBoundingClientRect();
            brushStartSvgX = ((e.clientX - rect.left) / rect.width) * 100;
            startX = e.clientX;
            brushRect.setAttribute('x', brushStartSvgX.toFixed(2));
            brushRect.setAttribute('width', '0');
            brushRect.style.opacity = '1';
        });

        this.interactiveArea.addEventListener('mousemove', (e) => {
            const points = (this as any)._points;
            if (!points || points.length === 0) return;

            const rect = this.svgElement!.getBoundingClientRect();
            const mouseX = ((e.clientX - rect.left) / rect.width) * 100;

            if (isBrushing) {
                const width = Math.abs(mouseX - brushStartSvgX);
                const x = Math.min(mouseX, brushStartSvgX);
                brushRect.setAttribute('x', x.toFixed(2));
                brushRect.setAttribute('width', width.toFixed(2));
            } else {
                // Find closest point by X coordinate
                let closest = points[0];
                let minDiff = Math.abs(mouseX - closest.x);
                for (const p of points) {
                    const diff = Math.abs(mouseX - p.x);
                    if (diff < minDiff) {
                        closest = p;
                        minDiff = diff;
                    }
                }

                this.hoverDot!.style.opacity = '1';

                const tooltipX = (closest.x / 100) * rect.width;
                const tooltipY = (closest.y / 40) * rect.height;

                this.hoverDot!.style.left = `${tooltipX}px`;
                this.hoverDot!.style.top = `${tooltipY}px`;

                this.tooltip!.textContent = `${closest.val.toLocaleString()}`;
                this.tooltip!.style.display = 'block';
                this.tooltip!.style.left = `${tooltipX}px`;
                this.tooltip!.style.top = `${tooltipY - 30}px`;
            }
        });

        const finishBrush = (e: MouseEvent) => {
            if (!isBrushing) return;
            isBrushing = false;
            brushRect.style.opacity = '0';
            
            const diffX = Math.abs(e.clientX - startX);
            if (diffX > 5) {
                // Was a brush selection
                const rect = this.svgElement!.getBoundingClientRect();
                const endSvgX = ((e.clientX - rect.left) / rect.width) * 100;
                
                const minX = Math.min(brushStartSvgX, endSvgX);
                const maxX = Math.max(brushStartSvgX, endSvgX);

                const points = (this as any)._points || [];
                let startIndex = 0;
                let endIndex = points.length - 1;

                for (let i = 0; i < points.length; i++) {
                    if (points[i].x >= minX) {
                        startIndex = i;
                        break;
                    }
                }
                for (let i = points.length - 1; i >= 0; i--) {
                    if (points[i].x <= maxX) {
                        endIndex = i;
                        break;
                    }
                }

                if (startIndex < endIndex) {
                    this.dispatchEvent(new CustomEvent('brush-selection', {
                        bubbles: true,
                        composed: true,
                        detail: { startIndex, endIndex }
                    }));
                }
            } else {
                // Was just a click
                this.dispatchEvent(new CustomEvent('explore-data', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        title: this._title,
                        data: this._data,
                        labels: this._labels
                    }
                }));
            }
        };

        this.interactiveArea.addEventListener('mouseup', finishBrush);
        this.interactiveArea.addEventListener('mouseleave', (e) => {
            if (isBrushing) finishBrush(e);
            if (this.hoverDot) this.hoverDot.style.opacity = '0';
            if (this.tooltip) this.tooltip.style.display = 'none';
        });
    }

    render() {
        if (!this.shadowRoot) return;

        this.shadowRoot.innerHTML = `
            <style>
                :host {
                    display: block;
                    width: 100%;
                    height: 100%;
                    position: relative;
                    font-family: var(--font-main);
                }
                .chart-container {
                    width: 100%;
                    height: 100%;
                    position: relative;
                    cursor: pointer; /* Suggests it is clickable */
                }
                svg {
                    width: 100%;
                    height: 100%;
                    overflow: hidden;
                }
                .line {
                    fill: none;
                    stroke: var(--accent);
                    stroke-width: 1.5;
                    stroke-linecap: round;
                    stroke-linejoin: round;
                    filter: url(#filt-sketch-${'app-line-chart'.split('').reduce((a, b) => a + b.charCodeAt(0), 0)});
                }
                .hover-circle {
                    fill: var(--bg-main, #111);
                    stroke: var(--accent);
                    stroke-width: 2;
                    opacity: 0;
                    transition: opacity 0.1s ease;
                    pointer-events: none;
                }
                .interactive-area {
                    fill: transparent;
                    cursor: crosshair;
                }
                .brush-selection {
                    fill: rgba(245, 158, 11, 0.2);
                    opacity: 0;
                    pointer-events: none;
                }
                .hover-dot {
                    position: absolute;
                    width: 8px;
                    height: 8px;
                    background: #fff;
                    border: 2px solid var(--accent);
                    border-radius: 50%;
                    transform: translate(-50%, -50%);
                    opacity: 0;
                    pointer-events: none;
                    transition: opacity 0.2s;
                    z-index: 10;
                }
                .tooltip {
                    position: absolute;
                    background: rgba(0, 0, 0, 0.8);
                    color: white;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    pointer-events: none;
                    display: none;
                    transform: translateX(-50%);
                    white-space: nowrap;
                    z-index: 10;
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.5);
                }
                .expand-icon {
                    position: absolute;
                    top: 4px;
                    right: 4px;
                    opacity: 0;
                    transition: opacity 0.2s;
                    color: var(--text-dim);
                    pointer-events: none;
                }
                .chart-container:hover .expand-icon {
                    opacity: 1;
                }
            </style>

            ${injectAesthetic('app-line-chart')}

            <div class="chart-container">
                <div class="tooltip" id="tooltip"></div>
                <div class="hover-dot" id="hover-dot"></div>
                
                <svg viewBox="0 0 100 40" preserveAspectRatio="none" id="chart-svg" style="height: calc(100% - 24px);">
                    <rect class="brush-selection" id="brush-selection" y="0" height="40" width="0" x="0"></rect>
                    <path class="line" id="chart-line" d="M2 35 L98 35" vector-effect="non-scaling-stroke"></path>
                    <rect class="interactive-area" id="interactive-area" width="100" height="40"></rect>
                </svg>
                <div id="x-axis-labels"></div>
            </div>
        `;

        this.svgElement = this.shadowRoot?.querySelector('#chart-svg') as SVGSVGElement;
        this.pathElement = this.shadowRoot?.querySelector('#chart-line') as SVGPathElement;
        this.hoverDot = this.shadowRoot?.querySelector('#hover-dot') as HTMLDivElement;
        this.tooltip = this.shadowRoot?.querySelector('#tooltip') as HTMLDivElement;
        this.interactiveArea = this.shadowRoot?.querySelector('#interactive-area') as SVGRectElement;
        this.drawChart();
    }
}

if (!customElements.get('app-line-chart')) {
    customElements.define('app-line-chart', AppLineChart);
}
